import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { Navigation } from '../components/Navigation';
import { MathBackground } from '../components/MathBackground';
import { useLanguage } from '../lib/LanguageContext';
import { translations } from '../lib/translations';
const SERVICE_ID = 'service_3zc7inj';
const TEMPLATE_ID = 'template_19se775';
const PUBLIC_KEY = '0PnF_uKPHnA1HeFu5';
export function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    organization: '',
    email: '',
    message: ''
  });
  const {
    lang
  } = useLanguage();
  const t = translations[lang].contact;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, {
        from_name: form.name,
        organization: form.organization,
        reply_to: form.email,
        message: form.message
      }, PUBLIC_KEY);
      setSubmitted(true);
    } catch (err) {
      setError(t.error);
    } finally {
      setSending(false);
    }
  };
  const inputStyle: React.CSSProperties = {
    fontFamily: '"IBM Plex Sans", sans-serif',
    fontWeight: 300,
    fontSize: '1rem',
    color: 'var(--black)',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '1px solid var(--grey-300)',
    padding: '0.75rem 0',
    width: '100%',
    outline: 'none',
    borderRadius: 0,
    transition: 'border-color 0.2s ease'
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: '"IBM Plex Mono", monospace',
    fontSize: '0.65rem',
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: 'var(--grey-500)',
    display: 'block',
    marginBottom: '0.5rem'
  };
  return <div style={{
    backgroundColor: 'var(--black)',
    minHeight: '100vh'
  }} data-id="element-441">
      <Navigation data-id="element-442" />

      {/* Page Header */}
      <section style={{
      position: 'relative',
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'flex-end',
      overflow: 'hidden',
      paddingBottom: '6rem',
      paddingTop: '64px'
    }} data-id="element-443">
        <MathBackground data-id="element-444" />
        <div style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: '64rem',
        margin: '0 auto',
        padding: '0 2rem',
        width: '100%'
      }} data-id="element-445">
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.15
        }} style={{
          fontFamily: '"IBM Plex Sans", sans-serif',
          fontWeight: 900,
          fontSize: 'clamp(40px, 7vw, 100px)',
          lineHeight: 1,
          letterSpacing: '-0.02em',
          color: 'var(--white)',
          margin: 0
        }} data-id="element-446">
            {t.hero}
          </motion.h1>
        </div>
      </section>

      {/* Contact Form */}
      <section style={{
      backgroundColor: 'var(--white)',
      padding: '8rem 0'
    }} data-id="element-447">
        <div style={{
        maxWidth: '48rem',
        margin: '0 auto',
        padding: '0 2rem'
      }} data-id="element-448">
          {/* Statement */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} style={{
          textAlign: 'center',
          marginBottom: '6rem',
          paddingBottom: '6rem',
          borderBottom: '1px solid rgba(0,0,0,0.08)'
        }} data-id="element-449">
            <p style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            fontSize: '1.125rem',
            color: 'var(--grey-700)',
            lineHeight: 2,
            margin: 0
          }} data-id="element-450">
              {t.intro}
            </p>
          </motion.div>

          {submitted ? <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} style={{
          textAlign: 'center',
          padding: '4rem 0'
        }} data-id="element-451">
              <p style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.75rem',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--grey-500)',
            marginBottom: '1.5rem'
          }} data-id="element-452">
                {t.receivedLabel}
              </p>
              <p style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            fontSize: '1.125rem',
            color: 'var(--grey-700)',
            lineHeight: 2
          }} data-id="element-453">
                {t.receivedMsg}
              </p>
            </motion.div> : <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6
        }} data-id="element-454">
              <p style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.75rem',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--grey-500)',
            marginBottom: '2rem'
          }} data-id="element-455">
                {t.directLabel}
              </p>
              <p style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            fontSize: '0.9375rem',
            color: 'var(--grey-500)',
            lineHeight: 1.8,
            marginBottom: '3rem',
            whiteSpace: 'pre-line'
          }} data-id="element-456">
                {t.directSub}
              </p>

              <form onSubmit={handleSubmit} data-id="element-457">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '3rem 4rem',
              marginBottom: '3rem'
            }} data-id="element-458">
                  <div data-id="element-459">
                    <label style={labelStyle} data-id="element-460">{t.labelName}</label>
                    <input style={inputStyle} type="text" required value={form.name} onChange={e => setForm(f => ({
                  ...f,
                  name: e.target.value
                }))} onFocus={e => e.currentTarget.style.borderBottomColor = 'var(--black)'} onBlur={e => e.currentTarget.style.borderBottomColor = 'var(--grey-300)'} data-id="element-461" />
                  </div>
                  <div data-id="element-462">
                    <label style={labelStyle} data-id="element-463">{t.labelOrg}</label>
                    <input style={inputStyle} type="text" value={form.organization} onChange={e => setForm(f => ({
                  ...f,
                  organization: e.target.value
                }))} onFocus={e => e.currentTarget.style.borderBottomColor = 'var(--black)'} onBlur={e => e.currentTarget.style.borderBottomColor = 'var(--grey-300)'} data-id="element-464" />
                  </div>
                </div>

                <div style={{
              marginBottom: '3rem'
            }} data-id="element-465">
                  <label style={labelStyle} data-id="element-466">{t.labelEmail}</label>
                  <input style={inputStyle} type="email" required value={form.email} onChange={e => setForm(f => ({
                ...f,
                email: e.target.value
              }))} onFocus={e => e.currentTarget.style.borderBottomColor = 'var(--black)'} onBlur={e => e.currentTarget.style.borderBottomColor = 'var(--grey-300)'} data-id="element-467" />
                </div>

                <div style={{
              marginBottom: '4rem'
            }} data-id="element-468">
                  <label style={labelStyle} data-id="element-469">{t.labelMessage}</label>
                  <textarea style={{
                ...inputStyle,
                resize: 'none',
                minHeight: '8rem',
                borderBottom: '1px solid var(--grey-300)'
              }} required value={form.message} onChange={e => setForm(f => ({
                ...f,
                message: e.target.value
              }))} onFocus={e => e.currentTarget.style.borderBottomColor = 'var(--black)'} onBlur={e => e.currentTarget.style.borderBottomColor = 'var(--grey-300)'} data-id="element-470" />
                </div>

                {error && <p style={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 300,
              fontSize: '0.875rem',
              color: 'var(--red)',
              marginBottom: '1.5rem'
            }} data-id="element-471">
                    {error}
                  </p>}

                <button type="submit" disabled={sending} style={{
              fontFamily: '"IBM Plex Sans", sans-serif',
              fontWeight: 300,
              fontSize: '0.875rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: sending ? 'var(--grey-500)' : 'var(--red)',
              backgroundColor: 'transparent',
              border: `1px solid ${sending ? 'var(--grey-300)' : 'var(--red)'}`,
              borderRadius: 0,
              padding: '1rem 2.5rem',
              cursor: sending ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s ease, color 0.2s ease'
            }} onMouseEnter={e => {
              if (!sending) {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--red)';
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--white)';
              }
            }} onMouseLeave={e => {
              if (!sending) {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--red)';
              }
            }} data-id="element-472">
                  {sending ? t.sending : t.submit}
                </button>
              </form>
            </motion.div>}

          {/* Footer text */}
          <div style={{
          marginTop: '8rem',
          paddingTop: '4rem',
          borderTop: '1px solid rgba(0,0,0,0.08)',
          textAlign: 'center'
        }} data-id="element-473">
            <p style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--grey-500)',
            marginBottom: '0.5rem'
          }} data-id="element-474">
              White Box AI
            </p>
            <p style={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 300,
            fontSize: '0.875rem',
            color: 'var(--grey-500)'
          }} data-id="element-475">
              {t.footerSub}
            </p>
          </div>
        </div>
      </section>
    </div>;
}